import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { TariffInterface } from '../../../interface/tariff.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  disablePartnerTariffFilterApplied,
  fetchAllPartnerTariffs,
  fetchPartnerTariffCounts,
  fetchPartnerTariffFilters,
  refreshAllPartnerTariffs,
  setSortData,
  updatePartnerTariffFilters,
} from '../../../redux/slices/PartnerTariffSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DisableSwitchWarning from '../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import TariffSidePanel from '../../../shared-components/table-side-panel/TariffSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertDDMMYYYYtoISO from '../../../shared-functions/ConvertDDMMYYYYtoISO';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import tariffApi from '../../../utils/api/tariff';

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * Component for displaying partner tariffs
 * @returns {React.JSX.Element} Partner tariffs component
 */
function Partner() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    tariffs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    tariffCounts,
    sortField,
  } = useAppSelector((state) => state.partnerTariff);

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    notificationModal: false,
    rightCorner: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    sessions: tariffs,
    isPartner: true,
  });
  const [searchParameters, setSearchParameters] = useSearchParams();

  /**
   * Fetch data for partner tariffs
   * @param {any} data - Data to fetch
   */
  const fetchData = (data: any) => {
    dispatch(fetchAllPartnerTariffs(removeEmptyKeys({ ...data, partner: true })));
  };

  /**
   * Handle page nate limit change
   * @param {any} value - Value to change
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData(removeEmptyKeys({ ...queryParameters, ...value, sortField, sortBy }));
  };

  /**
   * Handle sort function
   * @param {any} sortData - Data to sort
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData(
      removeEmptyKeys({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiParameters.page,
        limit,
      }),
    );
  };

  /**
   * Update tariff availability status with backend
   * @param {string} id - Tariff ID
   * @param {boolean} checked - Availability status
   */
  const updateAvailability = (id: string, checked: boolean) => {
    tariffApi
      .editAvailabilityTariff(id, { isAvailable: checked }, true)
      .then(() => {
        setModalBoxStatus('close');
        fetchData({ page, limit });
        dispatch(fetchPartnerTariffCounts('partner'));
      })
      .catch((error: any) => {
        setModalBoxStatus('close');
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message || 'Failed to update availability status',
            code: error?.response?.data?.code,
            status: true,
          }),
        );
      });
  };

  /**
   * Handles availability switch change
   * @param {TariffInterface} tariff - Tariff data
   * @param {boolean} checked - New status
   */
  const handleAvailabilityChange = (tariff: TariffInterface, checked: boolean) => {
    if (checked) {
      updateAvailability(tariff._id, checked);
    } else {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: '',
        rightCorner: '',
        content: (
          <DisableSwitchWarning
            selectedId={tariff._id}
            onCancel={() => setModalBoxStatus('close')}
            message="Are you sure you want to disable this tariff?"
            onConfirm={() => updateAvailability(tariff._id, checked)}
          />
        ),
        notificationModal: true,
      });
    }
  };

  /**
   * Render availability switch button
   * @param {TariffInterface} tariff - Tariff data
   * @returns {React.JSX.Element} Rendered availability switch button
   */
  const renderAvailability = (tariff: TariffInterface) => (
    <div
      role="presentation"
      className="flex justify-center"
      onClick={(event: React.MouseEvent) => event.stopPropagation()}>
      {tariff.status !== 'expired' && (
        <div key={tariff.id} className="availability-switch">
          <SwitchButton
            checked={tariff.isActive}
            change={(checked: boolean) => {
              if (!permissionCheck('cpoTariffStatusUpdate')) {
                return;
              }
              handleAvailabilityChange(tariff, checked);
            }}
          />
        </div>
      )}
    </div>
  );

  /**
   * Handles the delete warning modal for a partner tariff
   * @param {TariffInterface} rowData - The tariff row data to delete
   * @param {React.MouseEvent} event - The mouse click event trigger
   */
  const handleDeleteWarning = (rowData: TariffInterface, event: React.MouseEvent) => {
    event.stopPropagation();
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      rightCorner: '',
      notificationModal: true,
      content: (
        <div className="delete-rating-modal">
          <h4>Delete This Rating?</h4>
          <p>Are you sure you want to delete this rating?</p>
          <span>This action cannot be undone.</span>
          <div className="delete-modal-actions">
            <MyButton
              label="Cancel"
              buttonType="outline"
              labelColorFill="6C757D"
              onClickFunc={() => setModalBoxStatus('close')}
            />
            <MyButton
              label="Delete"
              buttonType="error"
              labelColorFill="#ffffff"
              onClickFunc={() => {
                tariffApi
                  .deleteTariff(rowData._id, true)
                  .then(() => {
                    setModalBoxStatus('open');
                    setModalBoxDetail({
                      title: '',
                      rightCorner: '',
                      content: (
                        <SuccessNotification
                          title="Tariff Deleted Successfully"
                          description=""
                          notificationCloseTimer={3000}
                          onNotificationClose={() => {
                            setModalBoxStatus('close');
                            fetchData({
                              page,
                              limit,
                            });
                            dispatch(fetchPartnerTariffCounts('partner'));
                          }}
                        />
                      ),
                      notificationModal: true,
                    });
                  })
                  .catch((error: any) => {
                    setModalBoxStatus('close');
                    dispatch(
                      setNotificationMessage({
                        status: true,
                        message: error?.response?.data?.message || 'Failed to delete tariff',
                        type: 'error',
                        code: error?.response?.data?.code,
                      }),
                    );
                  });
              }}
            />
          </div>
        </div>
      ),
    });
  };

  useEffect(() => {
    if (isFilterUpdated || tariffs.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiParameters.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePartnerTariffFilterApplied());
    }
  }, [filters]);

  const column = [
    {
      label: 'Tariff Name',
      accessorKey: 'name' as keyof TariffInterface,
      sortAllow: true,
      queryParamsKey: 'tariffId',
      sidePanelComponent: <TariffSidePanel isPartner />,
      /**
       * Custom cell for scope name
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for scope name
       */
      customData: (currentData: TariffInterface) => (
        <div className="scope-name-cell">
          <div className="name">{currentData.name}</div>
        </div>
      ),
    },
    {
      label: 'Type',
      accessorKey: 'type' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for type
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for type
       */
      customData: (currentData: TariffInterface) => (
        <div className="type-cell">
          <div className="name">{currentData.type || '-'}</div>
        </div>
      ),
    },
    {
      label: 'Partner ID',
      accessorKey: 'accountCustomId' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for account custom ID
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for account custom ID
       */
      customData: (currentData: TariffInterface) => (
        <div className="account-custom-id-cell">
          <div className="name">{currentData.accountCustomId || '-'}</div>
        </div>
      ),
    },
    {
      label: 'Current Type',
      accessorKey: 'currentType' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for current type
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for current type
       */
      customData: (currentData: TariffInterface) => (
        <div className={currentData?.currentType === 'AC' ? 'ac-status' : 'dc-status'}>
          {currentData?.currentType}
        </div>
      ),
    },
    {
      label: 'Connector Rating (kW)',
      accessorKey: 'connectorRating' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for connector rating
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for connector rating
       */
      customData: (currentData: TariffInterface) => <div>{currentData.connectorRating.currentType} {ToFixedValue(currentData.connectorRating.capacityKW)} {currentData.connectorRating.unit}</div>,
    },
    // {
    //   label: 'Time Based Pricing Slots',
    //   accessorKey: 'isTimeBasedPricingEnabled' as keyof TariffInterface,
    //   sortAllow: true,
    //   /**
    //    * Custom cell for time based pricing slots
    //    * @param {any} currentData - Tariff interface
    //    * @returns {any} Custom cell
    //    */
    //   customData: (currentData: TariffInterface) => (
    //     <div
    //       className={currentData?.isTimeBasedPricingEnabled ? 'active-status' : 'inactive-status'}>
    //       <div className="status-dot" />
    //       {currentData?.isTimeBasedPricingEnabled ? 'Yes' : 'No'}
    //     </div>
    //   ),
    // },
    {
      label: 'Status',
      accessorKey: 'status' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for status
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for status
       */
      customData: (currentData: TariffInterface) => (
        <div className={`${currentData.status}-status`}>
          <div className="status-dot" />{' '}
          {currentData.status ? currentData.status.charAt(0).toUpperCase() + currentData.status.slice(1).toLowerCase() : '-'}
        </div>
      ),
    },
    {
      label: 'Enforcement Date',
      accessorKey: 'effectiveFrom' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for enforcement date
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell
       */
      customData: (currentData: TariffInterface) => (
        <div>{ConvertDDMMYYYYtoISO(currentData.effectiveFrom, true)}</div>
      ),
    },
    {
      label: 'Expiry Date',
      accessorKey: 'effectiveTo' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for expiry date
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell
       */
      customData: (currentData: TariffInterface) => (
        <div>{ConvertDDMMYYYYtoISO(currentData.effectiveTo, true)}</div>
      ),
    },
    {
      label: 'Availability',
      accessorKey: 'isActive' as keyof TariffInterface,
      customData: renderAvailability,
      hideWhenSidePanelOpen: true,
    },
    {
      label: 'Actions',
      accessorKey: 'actions' as keyof TariffInterface,
      sortAllow: false,
      sticky: 'right' as const,
      /**
       * Custom cell for actions
       * @param {TariffInterface} currentData - Tariff interface
       * @returns {React.JSX.Element} Custom cell for actions
       */
      customData: (currentData: TariffInterface): React.JSX.Element => (
        <div
          className="action-button-group"
          role="presentation"
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
          }}>
          {currentData.status !== 'expired' && (
            <div
              className={`action-button-item edit-color ${
                !(permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffUpdate'))
                  ? 'disable-action'
                  : ''
              }`}
              role="presentation"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (
                  !(permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffUpdate'))
                ) {
                  return;
                }

                navigate(`/ev-admin/tariffs/edit-tariff/${currentData.id}?isPartner=true`);
              }}>
              <EditIcon style={{ color: 'var(--primary-color)' }} />
            </div>
          )}

          <div
            className={`action-button-item delete-color ${
              !permissionCheck('partnerTariffDelete') ? 'disable-action' : ''
            }`}
            role="presentation"
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              if (
                !(permissionCheck('partnerTariffDelete') || permissionCheck('cpoTariffUpdate'))
              ) {
                return;
              }
              handleDeleteWarning(currentData, event);
            }}>
            <DeleteIcon style={{ color: 'var(--error)' }} />
          </div>
        </div>
      ),
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: 'S.No', accessorKey: '__serial__' },
    { label: 'Tariff Name', accessorKey: 'name' },
    { label: 'Type', accessorKey: 'type' },
    { label: 'Partner ID', accessorKey: 'accountCustomId' },
    { label: 'Current Type', accessorKey: 'currentType' },
    { label: 'Connector Rating (kW)', accessorKey: 'connectorRating' },
    { label: 'Time Based Pricing Slots', accessorKey: 'isTimeBasedPricingEnabled' },
    { label: 'Status', accessorKey: 'status' },
    { label: 'Enforcement Date', accessorKey: 'effectiveFrom' },
    { label: 'Expiry Date', accessorKey: 'effectiveTo' },
    { label: 'Created At', accessorKey: 'createdAt' },
    { label: 'Last Updated', accessorKey: 'updatedAt' },
  ];

  /**
   * Handle click on table row
   * @param {string} id - Tariff ID
   */
  const handleClickTableRow = (id: string) => {
    searchParameters.set('tariffId', id);
    setSearchParameters(searchParameters);
  }

  /**
   * Export current columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data to PDF or CSV
   * @param {string} type - File type to export
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);

    tariffApi
      .exportTariffData(
        removeEmptyKeys({
          ...queryParameters,
          partner: true,
          sortBy,
          sortField,
        }),
      )
      .then((response: any) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: TariffInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'effectiveFrom':
                return ConvertDDMMYYYYtoISO(row.effectiveFrom, true);

              case 'effectiveTo':
                return ConvertDDMMYYYYtoISO(row.effectiveTo, true);

              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDateTime(row.createdAt) : '-';

              case 'updatedAt':
                return row.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : '-';

              case 'connectorRating':
                return row.connectorRating?.name || '-';

              case 'isTimeBasedPricingEnabled':
                return row.isTimeBasedPricingEnabled ? 'Yes' : 'No';

              default:
                return (row as any)[col.accessorKey] ?? '-';
            }
          }),
        );

        setExportLoader(false);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'partner-tariff-list.csv');
          } else {
            exportPDF(headers, rows, 'partner-tariff-list.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message || 'Failed to export tariff data',
            code: error?.response?.data?.code,
            status: true,
          }),
        );
      });
  };

  /**
   * Auto refresh handler
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      refreshAllPartnerTariffs(
        removeEmptyKeys({
          ...queryParameters,
          page,
          limit,
          sortBy,
          sortField,
          partner: true,
        }),
      ),
    );
  };

  const topLeftContent = (
    <div className="connector-rating-list-top-left-content">
      <div className="counts">
        <p className="count">{(tariffCounts?.active || 0) + (tariffCounts?.inactive || 0)}</p>
      </div>

      <div className="status active">
        <div className="indicator" />
        <p className="label">Active</p>
        <p className="count">{tariffCounts?.active || 0}</p>
      </div>

      <div className="status inactive">
        <div className="indicator" />
        <p className="label">Inactive</p>
        <p className="count">{tariffCounts?.inactive || 0}</p>
      </div>
    </div>
  );

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData(removeEmptyKeys({ ...queryParameters, page, limit, sortBy, sortField }));
  };

  useEffect(() => {
    dispatch(fetchPartnerTariffCounts('partner'));
  }, [dispatch]);

  return (
    <div className="tariff-global-wrapper">
      <ModalBox
        open={modalBoxStatus === 'open'}
        header={false}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        notification={modalBoxDetail.notificationModal}
        rightCorner={modalBoxDetail.rightCorner}
      />

      <PageTitle title="Total Charging Tariffs" topLeftContent={topLeftContent}/>

      <CustomTable
        dataKey="Tariff"
        version="v2"
        header={column}
        data={tariffs}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        actionButton
        serialNo
        created
        lastUpdated
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updatePartnerTariffFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchPartnerTariffFilters}
        exportLoader={exportLoader}
        exportData={tariffs}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        onHiddenColumnsChange={setHiddenColumns}
        height={320}
        requiredRefreshToggle
        refreshRequired
        refreshFunction={handleAutoRefresh}
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        hoverLabel="Refresh"
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
        rowClickEvent={handleClickTableRow}
        rowClickSidePanel={<TariffSidePanel isPartner />}
        rowClickParamsKey="tariffId"
      />
    </div>
  );
}

export default Partner;
